import * as React from 'react';
import {
    GridComponent, ColumnsDirective, ColumnDirective, Selection, Inject, Toolbar, Edit,
    Filter, Page, CommandColumn, Resize, Reorder, ActionEventArgs
} from '@syncfusion/ej2-react-grids';
import { DebtCharge, Payment } from '../../models/ContractDetails';
import { EditSettingsModel } from '@syncfusion/ej2-react-grids';
import '../../node_modules/@syncfusion/ej2-base/styles/bootstrap.css';
import '../../node_modules/@syncfusion/ej2-buttons/styles/bootstrap.css';
import '../../node_modules/@syncfusion/ej2-popups/styles/bootstrap.css';
import '../../node_modules/@syncfusion/ej2-react-grids/styles/bootstrap.css';

interface DebtChargesDialogProps {
    isOpen: boolean;
    onClose: () => void;
    payment: Payment;
    onSave: (payment: Payment) => void;
}

const DebtChargesDialog: React.FC<DebtChargesDialogProps> = ({ isOpen, onClose, payment, onSave }) => {
    if (!isOpen) return null;

    // Local state to manage debt charges
    const [debtCharges, setDebtCharges] = React.useState<DebtCharge[]>([]);
    const [totalDebt, setTotalDebt] = React.useState<number>(0);

    // Grid reference
    let grid: GridComponent | null = null;

    React.useEffect(() => {
        if (payment && payment.debtCharges) {
            setDebtCharges([...payment.debtCharges]);
            calculateTotalDebt(payment.debtCharges);
        } else {
            setDebtCharges([]);
            setTotalDebt(0);
        }
    }, [payment]);

    const calculateTotalDebt = (charges: DebtCharge[]) => {
        const total = charges.reduce((sum, charge) => sum + Number(charge.amount), 0);
        setTotalDebt(total);
    };

    const editSettings: EditSettingsModel = {
        allowEditing: true, 
        allowAdding: true, 
        allowDeleting: true,
        mode: 'Dialog'
    };

    const toolbarOptions = ['Add', 'Edit', 'Delete', 'Update', 'Cancel'];

    // Handler for grid action events
    const actionBegin = (args: ActionEventArgs) => {
        if (args.requestType === 'save' && args.data) {
            const data = args.data as DebtCharge;
            
            // For new records, generate an ID
            if (!data.id) {
                data.id = `debt_${Date.now()}`;
            }
            
            let updatedCharges: DebtCharge[];
            
            if (args.action === 'add') {
                updatedCharges = [...debtCharges, data];
            } else {
                // For editing
                updatedCharges = debtCharges.map(charge => 
                    charge.id === data.id ? data : charge
                );
            }
            
            setDebtCharges(updatedCharges);
            calculateTotalDebt(updatedCharges);
        }
        
        if (args.requestType === 'delete' && args.data) {
            const updatedCharges = debtCharges.filter(charge => 
                charge.id !== args.data[0].id
            );
            setDebtCharges(updatedCharges);
            calculateTotalDebt(updatedCharges);
        }
    };

    const handleSave = () => {
        if (payment) {
            const amount = Number(payment.amount || 0);
            const otherCharges = Number(payment.otherCharges || 0);
            
            // Calculate new total amount
            const newTotalAmount = amount + totalDebt + otherCharges;
            
            const updatedPayment = {
                ...payment,
                debtCharges: debtCharges,
                debt: totalDebt,
                totalAmount: newTotalAmount
            };
            
            // Save changes and explicitly close dialog
            onSave(updatedPayment);
        }
        
        // Ensure dialog closes regardless
        onClose();
    };

    const modalStyle: React.CSSProperties = {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
    };

    const dialogStyle: React.CSSProperties = {
        backgroundColor: 'white',
        borderRadius: '5px',
        padding: '20px',
        width: '800px',
        maxWidth: '90%',
        maxHeight: '90%',
        display: 'flex',
        flexDirection: 'column'
    };

    const headerStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px'
    };

    const contentStyle: React.CSSProperties = {
        flex: 1,
        overflow: 'auto'
    };

    const footerStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '15px',
        paddingTop: '15px',
        borderTop: '1px solid #eee'
    };

    const buttonStyle: React.CSSProperties = {
        padding: '8px 16px',
        marginLeft: '10px',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
    };

    return (
        <div style={modalStyle} onClick={onClose}>
            <div 
                style={dialogStyle} 
                onClick={(e) => e.stopPropagation()}
            >
                <div style={headerStyle}>
                    <h2>Edit Debt Charges</h2>
                    <button 
                        onClick={onClose}
                        style={{ 
                            border: 'none', 
                            background: 'none', 
                            fontSize: '20px',
                            cursor: 'pointer'
                        }}
                    >
                        &times;
                    </button>
                </div>

                <div style={contentStyle}>
                    <GridComponent
                        dataSource={debtCharges}
                        ref={g => grid = g}
                        toolbar={toolbarOptions}
                        editSettings={editSettings}
                        actionBegin={actionBegin}
                        height="400px"
                    >
                        <ColumnsDirective>
                            <ColumnDirective field="id" headerText="ID" width="100" isPrimaryKey={true} visible={false} />
                            <ColumnDirective field="concept" headerText="Concept" width="250" />
                            <ColumnDirective field="amount" headerText="Amount" width="150" 
                                format="c2"
                                editType="numericedit"
                                edit={{ params: { format: 'n2' } }}
                            />
                        </ColumnsDirective>
                        <Inject services={[Selection, Toolbar, Edit, Filter, Page, CommandColumn, Resize, Reorder]} />
                    </GridComponent>
                </div>

                <div style={footerStyle}>
                    <div>
                        <strong>Total Debt: ${totalDebt.toFixed(2)}</strong>
                    </div>
                    <div>
                        <button 
                            style={{
                                ...buttonStyle,
                                backgroundColor: '#f44336',
                                color: 'white'
                            }}
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button 
                            style={{
                                ...buttonStyle,
                                backgroundColor: '#4CAF50',
                                color: 'white'
                            }}
                            onClick={handleSave}
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DebtChargesDialog;
